import { useCallback, useEffect, useState } from 'react';
import { pb } from '../lib/pocketbase.js';
import { useAuth } from '../auth/AuthContext.jsx';
import UserFormModal from '../components/UserFormModal.jsx';
import { formatDatum, userAnzeige } from '../lib/aktivitaet.js';

const ROLLE_LABEL = {
  admin: 'Administrator:in',
  fuehrungskraft: 'Führungskraft',
  vertrieb: 'Vertrieb',
};

const ROLLE_BADGE = {
  admin: 'bg-primary-100 text-primary-800 ring-primary-200',
  fuehrungskraft: 'bg-amber-100 text-amber-800 ring-amber-200',
  vertrieb: 'bg-slate-100 text-slate-700 ring-slate-200',
};

export default function AdminUsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await pb.collection('users').getFullList({
        sort: 'nachname,vorname,email',
        // Für die "gesperrt am / durch" -Sekundärinfo in der Zeile.
        expand: 'gesperrt_von',
      });
      setUsers(list);
    } catch (err) {
      setError(err?.message || 'Benutzer konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function openCreate() {
    setEditTarget(null);
    setModalOpen(true);
  }

  function openEdit(user) {
    setEditTarget(user);
    setModalOpen(true);
  }

  // Sperren statt Löschen: der User-Datensatz bleibt vollständig erhalten,
  // alle angelegt_von-/erstellt_von-Referenzen in Aktivitäten, Praxen,
  // Kontakten und Selektionen zeigen weiter auf ihn. Gesperrt wird nur der
  // Zugang — Login und Token-Refresh blockt pb/pb_hooks/user_gesperrt.pb.js,
  // laufende Sessions sterben dort per tokenKey-Erneuerung.
  async function handleSperrToggle(user) {
    if (user.id === currentUser?.id) return;

    const name = userAnzeige(user);
    const sperren = !user.gesperrt;
    const frage = sperren
      ? `Möchtest du ${name} wirklich sperren? Der User kann sich nicht mehr einloggen. Aktivitäten und Daten bleiben erhalten.`
      : `${name} entsperren? Der User kann sich danach wieder einloggen.`;

    if (!window.confirm(frage)) return;

    setBusyId(user.id);
    setError(null);
    try {
      // gesperrt_am/gesperrt_von werden im Hook autoritativ überschrieben —
      // hier mitgeschickt, damit das Feld auch ohne Hook konsistent bliebe.
      await pb.collection('users').update(user.id, {
        gesperrt: sperren,
        gesperrt_am: sperren ? new Date().toISOString() : '',
        gesperrt_von: sperren ? currentUser?.id ?? '' : '',
      });
      await load();
    } catch (err) {
      setError(
        err?.response?.message ||
          err?.message ||
          (sperren ? 'Sperren fehlgeschlagen.' : 'Entsperren fehlgeschlagen.')
      );
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Benutzerverwaltung</h1>
          <p className="mt-1 text-sm text-slate-600">
            Lege Mitarbeiter:innen an, vergib Rollen und setze Passwörter zurück.
            Scheidet jemand aus, sperre den Zugang — dabei bleiben Aktivitäten,
            Praxen und Selektionen der Person vollständig erhalten.
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={openCreate}>
          + Neuer Benutzer
        </button>
      </div>

      <div className="card space-y-4">
        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Name
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  E-Mail
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Rolle
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Erstellt am
                </th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {loading && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-sm text-slate-500">
                    Lade …
                  </td>
                </tr>
              )}
              {!loading && users.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-sm text-slate-500">
                    Keine Benutzer gefunden.
                  </td>
                </tr>
              )}
              {!loading &&
                users.map((u) => {
                  const istSelbst = u.id === currentUser?.id;
                  const istGesperrt = !!u.gesperrt;
                  const sperrHinweis = istGesperrt
                    ? `Gesperrt am ${formatDatum(u.gesperrt_am)}${
                        u.expand?.gesperrt_von
                          ? ` durch ${userAnzeige(u.expand.gesperrt_von)}`
                          : ''
                      }`
                    : undefined;
                  return (
                    <tr
                      key={u.id}
                      className={`hover:bg-slate-50 ${istGesperrt ? 'bg-slate-50/60' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={
                              istGesperrt
                                ? 'font-medium text-slate-400 line-through'
                                : 'font-medium text-slate-900'
                            }
                          >
                            {userAnzeige(u)}
                          </span>
                          {istGesperrt && (
                            <span
                              className="inline-flex items-center rounded-full bg-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-300"
                              title={sperrHinweis}
                            >
                              Gesperrt
                            </span>
                          )}
                        </div>
                        {istSelbst && <div className="text-xs text-primary-700">(du)</div>}
                        {istGesperrt && (
                          <div className="mt-0.5 text-xs text-slate-400">{sperrHinweis}</div>
                        )}
                      </td>
                      <td
                        className={`px-4 py-3 text-sm ${
                          istGesperrt ? 'text-slate-400' : 'text-slate-600'
                        }`}
                      >
                        {u.email}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
                            istGesperrt
                              ? 'bg-slate-100 text-slate-400 ring-slate-200'
                              : ROLLE_BADGE[u.rolle] ?? 'bg-slate-100 text-slate-700 ring-slate-200'
                          }`}
                        >
                          {ROLLE_LABEL[u.rolle] ?? u.rolle ?? '—'}
                        </span>
                      </td>
                      <td
                        className={`px-4 py-3 text-sm ${
                          istGesperrt ? 'text-slate-400' : 'text-slate-600'
                        }`}
                      >
                        {formatDatum(u.created)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-3">
                          <button
                            type="button"
                            className="text-sm font-medium text-primary-600 hover:text-primary-800"
                            onClick={() => openEdit(u)}
                          >
                            Bearbeiten
                          </button>
                          {/* Kein Sperren-Button in der eigenen Zeile — Self-Lockout
                              wäre nur noch per DB-Eingriff zu reparieren. Der Hook
                              hält denselben Riegel serverseitig. */}
                          {!istSelbst && (
                            <button
                              type="button"
                              className={`text-sm font-medium disabled:opacity-40 ${
                                istGesperrt
                                  ? 'text-emerald-600 hover:text-emerald-800'
                                  : 'text-amber-600 hover:text-amber-800'
                              }`}
                              disabled={busyId === u.id}
                              onClick={() => handleSperrToggle(u)}
                            >
                              {busyId === u.id
                                ? '…'
                                : istGesperrt
                                  ? 'Entsperren'
                                  : 'Sperren'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      <UserFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        user={editTarget}
        onSaved={() => load()}
      />
    </div>
  );
}
